import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeHelperBadge from "../utils/HomeHelperBadge";
import { BrandMark } from "../branding/BrandText";

// Load workflow files to build the project-type dropdown using their title/slug
const workflowContext = require.context(
  "../content/categories",
  false,
  /\.workflow\.json$/
);

const CATEGORY_OPTIONS = workflowContext.keys().map((key) => {
  const data = workflowContext(key);
  const slug = data.slug || key.replace("./", "").replace(".workflow.json", "");
  const title = data.title || slug.replace(/-/g, " ");
  return { slug, title };
});

const FINANCING_STORAGE_KEY = "financing_data";

function loadFinancingFromStorage() {
  try {
    const raw = localStorage.getItem(FINANCING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

export default function FinancingWidget({ zip: initialZip, category }) {
  const navigate = useNavigate();
  const location = useLocation();

  const storedFinancing = useMemo(() => loadFinancingFromStorage(), []);

  const [zip, setZip] = useState(() => {
    if (initialZip) return initialZip;
    try {
      const fromUser = localStorage.getItem("user_zip");
      const fromHh = localStorage.getItem("hh_zip");
      const candidate = fromUser || fromHh || "";
      return candidate && /^\d{5}$/.test(candidate) ? candidate : "";
    } catch (err) {
      return "";
    }
  });

  const [projectType, setProjectType] = useState(() => {
    if (category) return category;
    try {
      return localStorage.getItem("user_category") || "";
    } catch (err) {
      return "";
    }
  });

  const [creditScoreRange, setCreditScoreRange] = useState(
    storedFinancing?.creditScoreRange || "good"
  );
  const [interestRate, setInterestRate] = useState(() => {
    const fromStorage = storedFinancing?.interestRate;
    if (typeof fromStorage === "number") {
      return Math.max(4.5, fromStorage);
    }
    return 6.5;
  });
  const [projectValue, setProjectValue] = useState(
    storedFinancing?.projectValue || 12000
  );
  const [downPaymentPercent, setDownPaymentPercent] = useState(() => {
    if (
      storedFinancing &&
      typeof storedFinancing.downPayment === "number" &&
      typeof storedFinancing.projectValue === "number" &&
      storedFinancing.projectValue > 0
    ) {
      const pct =
        (storedFinancing.downPayment / storedFinancing.projectValue) * 100;
      // round to nearest 0.5 like the calculator
      return Math.round(pct * 2) / 2;
    }
    return 10;
  });

  const [estimatedApproval, setEstimatedApproval] = useState(
    storedFinancing?.estimatedApproval || 0
  );
  const [monthlyPayment, setMonthlyPayment] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState("");
  const [hasCalculated, setHasCalculated] = useState(false);

  const getDownPaymentAmount = () => {
    const value = Number(projectValue) || 0;
    const pct = Number(downPaymentPercent) || 0;
    return Math.round((value * pct) / 100);
  };

  const computeMonthlyPayment = (amount, ratePercent) => {
    if (!amount || amount <= 0) return null;
    const termYears = 5;
    const monthlyRate = ratePercent ? ratePercent / 100 / 12 : 0;
    const n = termYears * 12;

    if (!monthlyRate) {
      return Math.round(amount / n);
    }

    const payment = (amount * monthlyRate) /
      (1 - Math.pow(1 + monthlyRate, -n));
    return Math.round(payment);
  };

  useEffect(() => {
    if (!storedFinancing) return;

    if (storedFinancing.creditScoreRange) {
      setCreditScoreRange(storedFinancing.creditScoreRange);
    }
    if (typeof storedFinancing.interestRate === "number") {
      setInterestRate(Math.max(4.5, storedFinancing.interestRate));
    }
    if (typeof storedFinancing.projectValue === "number") {
      setProjectValue(storedFinancing.projectValue);
    }
    if (typeof storedFinancing.estimatedApproval === "number") {
      setEstimatedApproval(storedFinancing.estimatedApproval);
    }

    if (storedFinancing.estimatedApproval && !aiSummary) {
      setAiSummary("");
    }
  }, [storedFinancing]);

  useEffect(() => {
    const downPaymentAmount = getDownPaymentAmount();
    const base = Math.max(Number(projectValue) - downPaymentAmount, 0);
    const multiplier = multipliers[creditScoreRange] || 1.0;
    const approvalAmount = Math.round(base * multiplier);

    setEstimatedApproval(approvalAmount || 0);

    const payment = computeMonthlyPayment(approvalAmount, interestRate);
    if (payment !== null) {
      setMonthlyPayment(payment);
    }
  }, [projectValue, downPaymentPercent, creditScoreRange, interestRate]);

  useEffect(() => {
    const handleFinancingUpdate = (event) => {
      const detail = event?.detail || {};
      const next = detail.payload || {};

      if (detail.zip) {
        setZip(detail.zip);
      }
      if (detail.category && !category) {
        setProjectType(detail.category);
      }
      if (next.creditScoreRange) {
        setCreditScoreRange(next.creditScoreRange);
      }
      if (typeof next.interestRate === "number") {
        setInterestRate(next.interestRate);
      }
      if (typeof next.projectValue === "number") {
        setProjectValue(next.projectValue);
      }
      if (typeof next.downPayment === "number") {
        // Convert absolute down payment into a percentage of project value
        if (typeof next.projectValue === "number" && next.projectValue > 0) {
          const pct =
            (next.downPayment / next.projectValue) * 100;
          setDownPaymentPercent(Math.round(pct * 2) / 2);
        } else {
          // Fall back to using the current project value if available
          const currentValue = Number(projectValue) || 0;
          if (currentValue > 0) {
            const pct = (next.downPayment / currentValue) * 100;
            setDownPaymentPercent(Math.round(pct * 2) / 2);
          }
        }
      }
      if (typeof next.estimatedApproval === "number") {
        setEstimatedApproval(next.estimatedApproval);
        const payment = computeMonthlyPayment(
          next.estimatedApproval,
          typeof next.interestRate === "number"
            ? next.interestRate
            : interestRate
        );
        if (payment !== null) {
          setMonthlyPayment(payment);
        }
      }
    };

    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener(
        "homehelper:financingUpdate",
        handleFinancingUpdate
      );
    }

    return () => {
      if (typeof window !== "undefined" && window.removeEventListener) {
        window.removeEventListener(
          "homehelper:financingUpdate",
          handleFinancingUpdate
        );
      }
    };
  }, [category, interestRate]);

  const isCategoryProvided = !!category;

  const multipliers = {
    excellent: 1.2,
    good: 1.0,
    moderate: 0.7,
    poor: 0.5,
  };

  const calculate = async () => {
    setError("");

    const trimmedZip = (zip || "").trim();
    if (!/^\d{5}$/.test(trimmedZip)) {
      setError("ZIP is required to estimate financing.");
      return;
    }

    const effectiveCategory = projectType || category;
    if (!effectiveCategory) {
      setError("Please select a project type.");
      return;
    }

    const downPaymentAmount = getDownPaymentAmount();

    await fetchInsight({
      category: effectiveCategory,
      project_value: Number(projectValue) || null,
      interest_rate: Number(interestRate) || null,
      down_payment: downPaymentAmount,
      credit_rating: creditScoreRange,
    });

    setHasCalculated(true);
  };

  const fetchInsight = async (payload) => {
    try {
      setLoadingSummary(true);
      setAiSummary("");

      const response = await fetch(
        "https://api.home-helper.com/api/financing/insight",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Request failed");
      }

      if (!response.body) {
        const text = await response.text();
        setAiSummary(text);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        if (chunk) {
          setAiSummary((prev) => prev + chunk);
        }
      }
    } catch (err) {
      console.error("Financing insight error", err);
      setAiSummary(
        "We couldn't generate a financing summary right now. Please try again later."
      );
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleCTA = () => {
    const trimmedZip = (zip || "").trim();
    const effectiveCategory = projectType || category;

    if (!/^\d{5}$/.test(trimmedZip)) {
      setError("ZIP is required to continue.");
      return;
    }

    if (!effectiveCategory) {
      setError("Please select a project type.");
      return;
    }

    const downPaymentAmount = getDownPaymentAmount();

    const payload = {
      creditScoreRange,
      estimatedApproval,
      interestRate,
      downPayment: downPaymentAmount,
      projectValue,
      financingRequested: true,
    };

    try {
      localStorage.setItem(FINANCING_STORAGE_KEY, JSON.stringify(payload));
      localStorage.setItem("user_zip", trimmedZip);
      localStorage.setItem("hh_zip", trimmedZip);
      localStorage.setItem("user_category", effectiveCategory);
    } catch (err) {
      // ignore storage errors
    }

    const path = location.pathname || "";
    const onHomepage = path === "/";
    const onCategoryPage = /^\/\d{5}\//.test(path);

    if (onHomepage || !onCategoryPage) {
      // From homepage, tools, or any non-category route, go to the
      // category landing page for the selected zip + project type.
      navigate(`/${trimmedZip}/${effectiveCategory}`);
      return;
    }

    if (onCategoryPage) {
      const anchor =
        document.querySelector("[data-survey-anchor]") ||
        document.getElementById("survey-root");

      if (anchor) {
        anchor.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate(`/survey/${effectiveCategory}`, {
          state: { zip: trimmedZip, source: "financing-widget" },
        });
      }
    }
  };

  return (
    <div className="financing-widget-card" id="financing-widget">
      <h2 className="financing-widget-title">Explore Monthly Financing</h2>
      <p className="financing-widget-subtitle">
        See how your project could fit into a simple monthly payment.
      </p>
      <div className="financing-widget-layout">
        <div
          className={`financing-widget-left${
            hasCalculated || loadingSummary ? " financing-widget-left-submitted" : ""
          }`}
        >
          {error && <div className="financing-widget-error">{error}</div>}

          <div className="financing-widget-grid">
            <div className="financing-widget-field">
              <label>ZIP Code *</label>
              <input
                value={zip}
                onChange={(e) => {
                  setZip(e.target.value);
                  if (error) setError("");
                }}
                inputMode="numeric"
                maxLength={5}
                placeholder="Enter ZIP"
              />
            </div>

            {!isCategoryProvided && (
              <div className="financing-widget-field">
                <label>Project Type *</label>
                <select
                  value={projectType}
                  onChange={(e) => {
                    setProjectType(e.target.value);
                    if (error) setError("");
                  }}
                >
                  <option value="">Select a project type</option>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.slug} value={opt.slug}>
                      {opt.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="financing-widget-field">
              <label>Credit Score Range</label>
              <select
                value={creditScoreRange}
                onChange={(e) => setCreditScoreRange(e.target.value)}
              >
                <option value="excellent">Excellent (760+)</option>
                <option value="good">Good (700-759)</option>
                <option value="moderate">Fair (640-699)</option>
                <option value="poor">Below 640</option>
              </select>
            </div>

            <div className="financing-widget-field">
              <label>Interest Rate ({interestRate.toFixed(1)}%)</label>
              <input
                type="range"
                className="slider"
                min="4.5"
                max="9"
                step="0.1"
                value={interestRate}
                onChange={(e) =>
                  setInterestRate(parseFloat(e.target.value) || 0)
                }
              />
            </div>

            <div className="financing-widget-field">
              <label>
                Project Value (${projectValue.toLocaleString()})
              </label>
              <input
                type="range"
                className="slider"
                min="1000"
                max="180000"
                step="500"
                value={projectValue}
                onChange={(e) =>
                  setProjectValue(parseInt(e.target.value, 10) || 0)
                }
              />
            </div>

            <div className="financing-widget-field">
              <label>
                Down Payment ({downPaymentPercent}% – $
                {getDownPaymentAmount().toLocaleString()})
              </label>
              <input
                type="range"
                className="slider"
                min="0"
                max="20"
                step="0.5"
                value={downPaymentPercent}
                onChange={(e) =>
                  setDownPaymentPercent(parseFloat(e.target.value) || 0)
                }
              />
            </div>
          </div>

          {!hasCalculated && (
            <div className="financing-widget-actions financing-widget-actions--left">
              <button
                className="financing-widget-calc"
                type="button"
                onClick={calculate}
                disabled={loadingSummary}
              >
                {loadingSummary
                  ? "Calculating estimate…"
                  : "Calculate Estimate"}
              </button>
            </div>
          )}

          {/* Disclaimer and CTA now live on the right column below the summary */}
        </div>

        <div className="financing-widget-right">
          <div className="financing-widget-ai">
            <div className="financing-widget-ai-header">
              <div className="financing-widget-ai-badge">
                <HomeHelperBadge />
              </div>
              <div className="financing-widget-ai-heading">
                <h3>Financing Likelihood Summary</h3>
              </div>

              {!loadingSummary && !aiSummary && (
                <p className="placeholder financing-widget-ai-placeholder">
                  Run a calculation above to see your <BrandMark /> financing
                  summary.
                </p>
              )}
            </div>

            <div className="financing-widget-results">
              <div className="financing-widget-amount">
                <div className="label">Estimated Approval</div>
                <div className="value">
                  {estimatedApproval
                    ? `$${estimatedApproval.toLocaleString()}`
                    : "—"}
                </div>
              </div>
              <div className="financing-widget-amount">
                <div className="label">Estimated Monthly Payment</div>
                <div className="value">
                  {monthlyPayment
                    ? `$${monthlyPayment.toLocaleString()}`
                    : "—"}
                </div>
              </div>
            </div>

            <div className="financing-widget-ai-box">
              {aiSummary && <p>{aiSummary}</p>}
              {loadingSummary && <span className="caret">▌</span>}
            </div>
            {hasCalculated && (
              <button
                className="financing-widget-cta"
                type="button"
                onClick={handleCTA}
              >
                Get Started on Your Project
              </button>
            )}
          </div>

          <p className="financing-widget-disclaimer">
            This is a soft estimate. Your credit is NOT checked and will not
            affect your score.
          </p>
        </div>
      </div>
    </div>
  );
}
